import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { debounce } from 'lodash';
import type { Selection } from '@react-types/shared';
import type {
  Asset,
  QuickSearchResult,
  VisualMatch as VisualMatchType,
} from '@frameio/graphql-client/generated';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { getUploadDisplayDate } from '@frameio/shared/utils';
import type { OnRequestClose } from '@frameio/vapor';
import { ListItem, ListSection, usePrevious } from '@frameio/vapor';
import { useSetAssetClipMatch } from '../../../hooks';
import {
  useQuickSearchActiveTab,
  useQuickSearchModal,
  useSearchSession,
  useTrackAiTabSelected,
  useTrackFilterSelected,
  useTrackSearchQueryInitiated,
  useTrackSearchResultsClicked,
  useTrackSeeAiResultsClicked,
  useTrackSeeStandardResultsClicked,
  useTrackStandardTabSelected,
} from '../../../shared/contexts';
import type {
  TrackResultType,
  TrackSearchScope,
} from '../../../shared/contexts/SearchAnalyticsContext/types';
import { getTabSelected } from '../../../shared/contexts/SearchAnalyticsContext/utils';
import { useIsSemanticSearchEnabled } from '../../../shared/hooks';
import type { SectionId } from '../../../shared/types';
import { LEXICAL_SECTION_ID, NLP_SECTION_ID } from '../../../shared/types';
import {
  getDaysSinceCreated,
  getMatchTypeTrackingValues,
  getResultItemLink,
  getResultSourceFromSectionId,
} from '../../../shared/utils';
import { isNlpResult } from '../../../utils';
import {
  useFlattenedSearchResults,
  useGetQuickSearchResults,
  useQuickSearchAppliedFilterTypes,
  useQuickSearchResultsAnalytics,
  useQuickSearchTabbedViewModel,
} from '../../hooks';
import type {
  FilterKeys,
  QuickSearchResults,
  QuickSearchResultSection,
} from '../../types';
import { getActiveListItemDetails } from '../../utils';
import { QuickSearchList } from '../QuickSearchList';
import { QuickSearchListItem } from '../QuickSearchListItem';
import { QuickSearchSectionHeader } from '../QuickSearchSectionHeader';
import {
  QuickSearchTabBar,
  QuickSearchTabEmptyState,
} from '../QuickSearchTabBar';
import { QuickSearchListDivider } from '../shared';
import type { QuickSearchResultTypes } from '../shared/QuickSearchResultTypes';
import TEXT from './generated/TEXT/QuickSearchDialogContent';
import {
  getQuickSearchTabbedAffordances,
  isQuickSearchTabUpsellListItemKey,
} from './getQuickSearchTabbedAffordances';
import { useResultsListFocusOnTabChange } from './hooks';
import { createQuickSearchTabUpsellListItem } from './QuickSearchTabUpsellListItem';

TEXT.DEFINE({
  /** Accessible label for individual search result item */
  listItemAriaLabel: ({ name }: { name: string }): string =>
    `Search result: ${name}`,
  /** Accessible label for the entire quick search component */
  quickSearchAriaLabel: 'Quick Search',
});

const TOGGLE_FILTER_DEBOUNCE = 200;

/**
 * Generates a unique key for a quick search result list item.
 * This is used for React's `key` prop to ensure efficient re-rendering of the list.
 * In multi-mode, includes the sectionId to differentiate items in different sections.
 */
function generateQuickSearchResultListItem(
  item: QuickSearchResult,
  position: number,
  sectionId?: string
): string | undefined {
  const sectionPrefix = sectionId ? `${sectionId}:` : '';
  return item?.__typename
    ? `${sectionPrefix}${item.__typename}:${item.result.id}:${position}`
    : undefined;
}

/**
 * Props for the `QuickSearchDialogContent` component.
 */
export type QuickSearchDialogContentProps = {
  isMobile: boolean;
  onDialogClose: OnRequestClose;
};

/**
 * Renders the content of the Quick Search dialog.
 *
 * This component orchestrates the main user interface for quick search,
 * including the search input, filters, and results list. It is designed to be
 * used within the `QuickSearchDialog`.
 */
export const QuickSearchDialogContent = forwardRef<
  HTMLInputElement,
  QuickSearchDialogContentProps
>(({ isMobile, onDialogClose }, forwardedRef) => {
  const isSemanticSearchEnabled = useIsSemanticSearchEnabled();
  const activeProjectId = useActiveProjectId();

  const {
    isModalOpen,
    query: searchQuery,
    filters: searchFilters,
    setQuery,
    toggleFilter,
    clearAllFilters,
    clearQuery,
    requestVariables,
    focusedKey,
    setFocusedKey,
    scopedToProjectId,
  } = useQuickSearchModal();

  const appliedFilterTypes = useQuickSearchAppliedFilterTypes();

  const { activeTab, setActiveTab } = useQuickSearchActiveTab();

  // Track when the input query is ahead of the search query (during debounce)
  const [isInputQueryStale, setIsInputQueryStale] = useState(false);

  // Keep a ref to the current filters for tracking filter changes
  const searchFiltersRef = useRef(searchFilters);

  const previousSearchQuery = usePrevious(searchQuery);
  const previousScopedToProjectId = usePrevious(scopedToProjectId);
  const [isInitial, setIsInitial] = useState(true);

  useEffect(() => {
    const hasSearchQueryChanged =
      previousSearchQuery !== undefined && searchQuery !== previousSearchQuery;
    const hasSearchFiltersChanged = searchFiltersRef.current !== searchFilters;
    const hasScopeChanged =
      previousScopedToProjectId !== undefined &&
      scopedToProjectId !== previousScopedToProjectId;

    if (hasSearchQueryChanged || hasSearchFiltersChanged || hasScopeChanged) {
      setIsInitial(false);
    }
  }, [
    searchQuery,
    previousSearchQuery,
    searchFilters,
    scopedToProjectId,
    previousScopedToProjectId,
  ]);

  useEffect(() => {
    searchFiltersRef.current = searchFilters;
  }, [searchFilters]);

  const searchResults = useGetQuickSearchResults({
    requestVariables,
    skip: !requestVariables.accountId || searchQuery === '',
    isSearchQueryStale: isInputQueryStale,
    isInitial,
  });
  const searchCycleKey = useMemo(
    () => JSON.stringify(requestVariables),
    [requestVariables]
  );

  const handleViewAllClick =
    searchResults.listMode === 'MULTI'
      ? searchResults.handleViewAllClick
      : undefined;

  const tabbedViewModel = useQuickSearchTabbedViewModel({ searchResults });

  const listViewModel = useMemo(() => {
    if (searchResults.listMode === 'MULTI_TABBED') {
      return tabbedViewModel!;
    }

    return {
      shouldShowLexicalDivider:
        searchResults.listMode !== 'SINGLE' && searchResults.items.length > 1,
      showTabBar: false,
      visibleSectionId: undefined,
      visibleItems: searchResults.items,
      visibleLoadingStatus: searchResults.loadingStatus,
    };
  }, [searchResults, tabbedViewModel]);

  const onTabChange = tabbedViewModel?.onTabChange ?? setActiveTab;
  /**
   * stable property for the tab selected
   * this is used for the current selected tab
   * for analytics purposes
   */
  const tabSelectedProperty =
    searchResults.listMode === 'MULTI_TABBED'
      ? getTabSelected(listViewModel.visibleSectionId)
      : undefined;
  const tabbedAffordances = useMemo(
    () =>
      getQuickSearchTabbedAffordances({
        activeTab,
        hasQueriesSettled: searchResults.hasQueriesSettled,
        listMode: searchResults.listMode,
        mediaIntelligenceResultsCount:
          searchResults.mediaIntelligenceResultsCount,
        standardResultsCount: searchResults.standardResultsCount,
        visibleSectionId: listViewModel.visibleSectionId,
      }),
    [
      activeTab,
      listViewModel.visibleSectionId,
      searchResults.hasQueriesSettled,
      searchResults.listMode,
      searchResults.mediaIntelligenceResultsCount,
      searchResults.standardResultsCount,
    ]
  );

  const flattenedItems = useFlattenedSearchResults(
    listViewModel.visibleItems,
    searchResults.listMode
  );

  const isModalOpenPrevious = usePrevious(isModalOpen);
  const trackSearchQueryInitiated = useTrackSearchQueryInitiated();
  const trackSearchResultsClicked = useTrackSearchResultsClicked();
  const trackFilterSelected = useTrackFilterSelected();
  const trackStandardTabSelected = useTrackStandardTabSelected();
  const trackAiTabSelected = useTrackAiTabSelected();
  const trackSeeStandardResultsClicked = useTrackSeeStandardResultsClicked();
  const trackSeeAiResultsClicked = useTrackSeeAiResultsClicked();
  const {
    resetSearchSession,
    setSearchInitiatedAt,
    resetSearchInitiatedAt,
    setSearchQuery,
  } = useSearchSession();

  const handleTrackResultClick = useCallback(
    (resultItem: QuickSearchResult, sectionId?: SectionId) => {
      if (searchQuery.length === 0 || flattenedItems.length === 0) {
        return;
      }

      const resultPosition = flattenedItems.indexOf(resultItem) + 1;
      const resultName = resultItem.result.name;
      let resultType;
      if (resultItem.__typename === 'ProjectResult') {
        resultType = 'Project';
      } else if ((resultItem.result as Asset).assetType === 'FOLDER') {
        resultType = 'Folder';
      } else {
        resultType = 'Asset';
      }

      // Use displayDate for assets (with VersionStack priority), insertedAt for projects
      let dateForTracking: string | null | undefined;
      if (resultItem.__typename === 'ProjectResult') {
        dateForTracking = resultItem.result.insertedAt;
      } else if (resultItem.__typename === 'AssetResult') {
        dateForTracking = getUploadDisplayDate(resultItem.result);
      }

      const daysSinceCreated = getDaysSinceCreated(dateForTracking);
      const resultSource = getResultSourceFromSectionId(sectionId);

      trackSearchResultsClicked({
        daysSinceUploaded: daysSinceCreated,
        filterApplied: appliedFilterTypes,
        matchType: getMatchTypeTrackingValues(resultItem.matches?.nodes ?? []),
        resultName,
        resultPosition,
        resultSource,
        resultType: resultType as TrackResultType,
        searchQuery,
        tabSelected: tabSelectedProperty,
      });
    },
    [
      appliedFilterTypes,
      flattenedItems,
      searchQuery,
      tabSelectedProperty,
      trackSearchResultsClicked,
    ]
  );

  const handleOnActivateItem = useCallback(() => {
    const details = getActiveListItemDetails(focusedKey);
    const { isItemSelected, itemId, sectionId } = details;

    if (!isItemSelected) return;

    const item = flattenedItems.find((result) => result.result.id === itemId);

    if (item) {
      handleTrackResultClick(item, sectionId);
    }
  }, [flattenedItems, handleTrackResultClick, focusedKey]);

  /**
   * Tracked imperatively at dispatch time rather than in a useEffect keyed off
   * isInputQueryStale: that flag is relayed up from the list component through
   * its own effect, one render behind the raw keystroke comparison, so it can
   * still be true here when results-displayed (gated only on hasQueriesSettled)
   * has already fired for the same query. Firing here guarantees query-initiated
   * always precedes the fetch it triggers.
   */
  const lastTrackedQueryRef = useRef<string | null>(null);

  const handleRequestSearch = useCallback(
    (query: string) => {
      const trimmedQuery = query.trim();
      setQuery(trimmedQuery);
      setSearchQuery(trimmedQuery);

      if (trimmedQuery && lastTrackedQueryRef.current !== trimmedQuery) {
        lastTrackedQueryRef.current = trimmedQuery;
        trackSearchQueryInitiated({
          filterApplied: appliedFilterTypes,
          searchQuery: trimmedQuery,
        });
        setSearchInitiatedAt(performance.now());
      }
    },
    [
      appliedFilterTypes,
      setQuery,
      setSearchInitiatedAt,
      setSearchQuery,
      trackSearchQueryInitiated,
    ]
  );

  const handleInputQueryStaleChange = useCallback((isStale: boolean) => {
    setIsInputQueryStale(isStale);
  }, []);

  const { scheduleResultsListFocus, resultsListRef: handleResultsListRef } =
    useResultsListFocusOnTabChange({
      visibleItemsCount: listViewModel.visibleItems.length,
      visibleLoadingStatus: listViewModel.visibleLoadingStatus,
    });

  const handleToggleFilter = useCallback(
    (filter: FilterKeys, value: boolean) => {
      if (value === true)
        trackFilterSelected({
          filter,
          filtersMap: searchFiltersRef.current,
        });
      toggleFilter(filter, value);
    },
    [toggleFilter, trackFilterSelected]
  );

  const handleDebouncedToggleFilter = useMemo(
    () =>
      debounce(handleToggleFilter, TOGGLE_FILTER_DEBOUNCE, { leading: true }),
    [handleToggleFilter]
  );

  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    setIsInitial(false);
  }, [clearAllFilters, setIsInitial]);

  const handleResetQueryState = useCallback(() => {
    clearQuery();
    resetSearchInitiatedAt();
    setSearchQuery('');
    setIsInitial(false);
    lastTrackedQueryRef.current = null;
  }, [clearQuery, resetSearchInitiatedAt, setSearchQuery, setIsInitial]);

  const handleOnRequestClose: OnRequestClose = useCallback(
    (evt) => {
      handleResetQueryState();
      onDialogClose(evt);
    },
    [onDialogClose, handleResetQueryState]
  );

  const handleOnSelectionChange = useCallback(
    (keys: Selection) => {
      const newlySelectedId = Array.from(keys)[0] as string;
      if (!newlySelectedId || newlySelectedId === focusedKey) return;
      if (isQuickSearchTabUpsellListItemKey(newlySelectedId)) return;
      setFocusedKey(newlySelectedId);
    },
    [setFocusedKey, focusedKey]
  );

  const setAssetClipMatch = useSetAssetClipMatch();

  const renderListItem = useCallback(
    (resultItem: QuickSearchResult, sectionId?: SectionId) => {
      const { result: item, __typename: type } = resultItem;
      const position = flattenedItems.indexOf(resultItem) + 1;

      const visualMatch = isNlpResult(resultItem)
        ? (resultItem.nlpMatches?.nodes?.find(
            (node) => node.__typename === 'VisualMatch'
          ) as VisualMatchType)
        : null;

      const href = getResultItemLink(
        item,
        type,
        visualMatch,
        setAssetClipMatch
      );

      const handleListItemAction = () =>
        handleTrackResultClick(resultItem, sectionId);

      const textValue = TEXT.listItemAriaLabel({ name: item.name });

      return (
        <ListItem
          key={generateQuickSearchResultListItem(
            resultItem,
            position,
            sectionId
          )}
          aria-label={textValue}
          as="div"
          href={href}
          onAction={handleListItemAction}
          textValue={textValue}
        >
          <QuickSearchListItem
            itemId={item.id}
            itemPosition={position}
            itemType={type as QuickSearchResultTypes}
            sectionId={sectionId}
            tabSelected={tabSelectedProperty}
          />
        </ListItem>
      );
    },
    [
      handleTrackResultClick,
      flattenedItems,
      tabSelectedProperty,
      setAssetClipMatch,
    ]
  );

  const shouldShowViewAll = useMemo(() => {
    if (searchResults.listMode !== 'MULTI') return false;

    return (
      searchResults.loadingStatus === undefined &&
      searchResults.items.some((section) => section.id === 'nlp')
    );
  }, [searchResults]);

  const onTabbedViewChange = tabbedViewModel?.onTabChange;
  const handleTabBarChange = useCallback(
    (sectionId: SectionId) => {
      if (sectionId === NLP_SECTION_ID) {
        trackAiTabSelected();
      } else {
        trackStandardTabSelected();
      }
      onTabbedViewChange?.(sectionId);
      scheduleResultsListFocus();
    },
    [
      onTabbedViewChange,
      trackAiTabSelected,
      trackStandardTabSelected,
      scheduleResultsListFocus,
    ]
  );

  const handleCtaAction = useCallback(
    (targetSectionId: SectionId) => {
      if (targetSectionId === NLP_SECTION_ID) {
        trackSeeAiResultsClicked();
      } else {
        trackSeeStandardResultsClicked();
      }
      onTabChange(targetSectionId);
    },
    [onTabChange, trackSeeAiResultsClicked, trackSeeStandardResultsClicked]
  );

  const tabBar = useMemo(() => {
    if (searchResults.listMode !== 'MULTI_TABBED') return null;
    if (!listViewModel.showTabBar) return null;

    return (
      <QuickSearchTabBar
        lexicalLoading={searchResults.isLexicalLoading}
        mediaIntelligenceResultsCount={
          searchResults.mediaIntelligenceResultsCount
        }
        nlpLoading={searchResults.isNlpLoading}
        onTabChange={handleTabBarChange}
        searchCycleKey={searchCycleKey}
        standardResultsCount={searchResults.standardResultsCount}
      />
    );
  }, [
    handleTabBarChange,
    listViewModel.showTabBar,
    searchCycleKey,
    searchResults,
  ]);

  const noResultsContent = useMemo(() => {
    if (!tabbedAffordances.emptyState) {
      return undefined;
    }

    const {
      activeTab: emptyStateTab,
      otherTabCount,
      targetSectionId,
    } = tabbedAffordances.emptyState;

    return (
      <QuickSearchTabEmptyState
        activeTab={emptyStateTab}
        onAction={() => handleCtaAction(targetSectionId)}
        otherTabCount={otherTabCount}
      />
    );
  }, [tabbedAffordances.emptyState, handleCtaAction]);

  const upsellListItem = useMemo(() => {
    if (!tabbedAffordances.upsell) {
      return null;
    }

    return createQuickSearchTabUpsellListItem({
      descriptor: tabbedAffordances.upsell,
      onAction: handleCtaAction,
    });
  }, [tabbedAffordances.upsell, handleCtaAction]);

  const renderListSection = useCallback(
    (section: QuickSearchResultSection) => {
      const isLexicalSection = section.id === LEXICAL_SECTION_ID;

      const shouldShowDivider =
        isLexicalSection && listViewModel.shouldShowLexicalDivider;

      const shouldAppendUpsellListItem = section.trailingListItem != null;
      const sectionKey =
        searchResults.listMode === 'MULTI_TABBED'
          ? `${section.id}:${searchCycleKey}`
          : section.id;

      const sectionProps = {
        divider: shouldShowDivider && (
          <QuickSearchListDivider paddingLeft={16} />
        ),
        isHeaderVisible: searchResults.listMode !== 'MULTI_TABBED',
        title: (
          <QuickSearchSectionHeader
            onViewAllClick={handleViewAllClick}
            section={section}
            shouldShowViewAll={shouldShowViewAll}
          />
        ),
      };

      if (shouldAppendUpsellListItem) {
        const staticChildren = [
          ...section.items.map((item) => renderListItem(item, section.id)),
          section.trailingListItem,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ] as any;

        return (
          <ListSection key={sectionKey} {...sectionProps}>
            {staticChildren}
          </ListSection>
        );
      }

      return (
        <ListSection key={sectionKey} {...sectionProps} items={section.items}>
          {(sectionItem: QuickSearchResult) =>
            renderListItem(sectionItem, section.id)
          }
        </ListSection>
      );
    },
    [
      renderListItem,
      searchResults.listMode,
      listViewModel.shouldShowLexicalDivider,
      shouldShowViewAll,
      handleViewAllClick,
      searchCycleKey,
    ]
  );

  const renderItems = useCallback(
    (item: QuickSearchResult | QuickSearchResultSection) => {
      if (searchResults.listMode === 'SINGLE') {
        return renderListItem(item as QuickSearchResult);
      }
      if (searchResults.listMode === 'MULTI_TABBED') {
        return renderListSection(item as QuickSearchResultSection);
      }
      // MULTI mode: renders sections with a list of items
      return renderListSection(item as QuickSearchResultSection);
    },
    [searchResults.listMode, renderListItem, renderListSection]
  );

  const visibleListItems = useMemo((): QuickSearchResults => {
    if (searchResults.listMode !== 'MULTI_TABBED') {
      return listViewModel.visibleItems;
    }

    if (
      listViewModel.visibleItems.length === 0 ||
      !listViewModel.visibleSectionId
    ) {
      return [];
    }

    return [
      {
        id: listViewModel.visibleSectionId,
        items: listViewModel.visibleItems as QuickSearchResult[],
        title: '',
        trailingListItem: upsellListItem,
      },
    ];
  }, [
    searchResults.listMode,
    listViewModel.visibleItems,
    listViewModel.visibleSectionId,
    upsellListItem,
  ]);

  useEffect(() => {
    // Reset analytics state on close
    if (isModalOpenPrevious && !isModalOpen) {
      resetSearchSession();
    }
  }, [isModalOpen, isModalOpenPrevious, resetSearchSession]);

  const hasSearchQuery = searchQuery.trim() !== '';
  let searchScope: TrackSearchScope | undefined;
  if (activeProjectId) {
    searchScope = scopedToProjectId ? 'CurrentProject' : 'AllProjects';
  }

  useQuickSearchResultsAnalytics({
    hasSearchQuery,
    searchQuery,
    hasQueriesSettled: searchResults.hasQueriesSettled,
    standardResultsCount: searchResults.standardResultsCount,
    mediaIntelligenceResultsCount: searchResults.mediaIntelligenceResultsCount,
    searchScope,
    activeTab: listViewModel.visibleSectionId,
  });

  return (
    <QuickSearchList
      ref={forwardedRef}
      aria-label={TEXT.quickSearchAriaLabel}
      filters={searchFilters}
      focusedKey={focusedKey}
      isInitial={isInitial}
      isMobile={isMobile}
      isSemanticSearchEnabled={isSemanticSearchEnabled}
      items={visibleListItems}
      listMode={searchResults.listMode}
      loading={listViewModel.visibleLoadingStatus}
      noResultsContent={noResultsContent}
      onActivateItem={handleOnActivateItem}
      onClearAllFilters={handleClearAllFilters}
      onInputQueryStaleChange={handleInputQueryStaleChange}
      onRequestClose={handleOnRequestClose}
      onRequestSearch={handleRequestSearch}
      onResetQueryState={handleResetQueryState}
      onSelectionChange={handleOnSelectionChange}
      onToggleFilter={handleDebouncedToggleFilter}
      resultsListRef={handleResultsListRef}
      resultsListResetKey={
        searchResults.listMode === 'MULTI_TABBED' ? searchCycleKey : undefined
      }
      searchQuery={searchQuery}
      sectionGap={16}
      showNlpResultsFooter={
        searchResults.listMode === 'MULTI_TABBED' &&
        activeTab === NLP_SECTION_ID
      }
      tabBar={tabBar}
    >
      {renderItems}
    </QuickSearchList>
  );
});

QuickSearchDialogContent.displayName = 'QuickSearchDialogContent';
